import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

export interface SelectOption {
  value: unknown;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'ui-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private el = inject(ElementRef);

  /** Options to display in the dropdown */
  options = input<SelectOption[]>([]);

  /** Placeholder when no value is selected */
  placeholder = input<string>('Select an option...');

  /** Compact mode: smaller trigger, like role-picker */
  compact = input<boolean>(false);

  /** Emit when value changes (for non-CVA usage) */
  valueChange = output<unknown>();

  // ── Internal state ──────────────────────────────────────────────────────

  isOpen = signal(false);

  /** Internal value (managed by CVA or direct binding) */
  protected _value: unknown = null;

  protected _disabled = false;

  // Dropdown position (position: fixed, relative to viewport)
  dropdownTop = signal<number | null>(0);
  dropdownBottom = signal<number | null>(null);
  dropdownLeft = signal(0);
  dropdownWidth = signal(160);

  @ViewChild('triggerBtn') triggerBtn!: ElementRef<HTMLButtonElement>;

  // ── Derived helpers ─────────────────────────────────────────────────────

  get selectedLabel(): string {
    const found = this.options().find((o) => o.value === this._value);
    return found?.label ?? '';
  }

  get hasValue(): boolean {
    return this._value !== null && this._value !== undefined && this._value !== '';
  }

  // ── Interaction ─────────────────────────────────────────────────────────

  toggle(): void {
    if (this._disabled) return;
    if (!this.isOpen()) {
      this.recalcPosition();
    }
    this.isOpen.update((v) => !v);
  }

  select(option: SelectOption): void {
    if (option.disabled) return;
    this._value = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.valueChange.emit(option.value);
    this.isOpen.set(false);
    this.cdr.markForCheck();
  }

  private recalcPosition(): void {
    const triggerEl = this.triggerBtn?.nativeElement;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    
    let containerTop = 0;
    let containerLeft = 0;
    let containerBottom = window.innerHeight;
    
    let parent = triggerEl.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (style.transform && style.transform !== 'none') {
        const pRect = parent.getBoundingClientRect();
        containerTop = pRect.top;
        containerLeft = pRect.left;
        containerBottom = pRect.bottom;
        break;
      }
      parent = parent.parentElement;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 300 && spaceAbove > spaceBelow) {
      // Open upwards if insufficient space below
      this.dropdownTop.set(null);
      this.dropdownBottom.set(containerBottom - rect.top + 4);
    } else {
      // Open downwards (default)
      this.dropdownTop.set(rect.bottom - containerTop + 4);
      this.dropdownBottom.set(null);
    }

    this.dropdownLeft.set(rect.left - containerLeft);
    this.dropdownWidth.set(Math.max(rect.width, 160));
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────

  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: unknown): void {
    this._value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ── Host listeners ──────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target as Node)) {
      if (this.isOpen()) {
        this.onTouched();
        this.isOpen.set(false);
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.onTouched();
      this.isOpen.set(false);
    }
  }

  ngOnDestroy(): void {
    this.isOpen.set(false);
  }
}
